import {
  IAccountComment,
  IAnonymousUser,
  IUserDataForMemberManagement,
  IUserLeaderboardPreferenceViewResult,
  IUserNames,
  WorkoutImportOrigin,
} from 'fitbeat.models';
import { UserProfile } from '../../../entities/user/userprofile';
import { IUserProfileRepository } from '../interfaces/iuserprofile-repository';

export class InMemoryUserProfileRepository implements IUserProfileRepository {
    private users = new Array<UserProfile>();
    public get(uid: string): Promise<UserProfile | undefined> {
        return new Promise((resolve) => {
            resolve(this.getUser(uid));
        });
    }

    public async getMemberInfoByGymName(
        gymName?: string, emailExclusionFilter?: string): Promise<IUserDataForMemberManagement[]> {
            return this.users.filter((u) => u.gymName === gymName);
    }

    public getUserProfileOrThrow(uid: string): Promise<UserProfile> {
      return new Promise((resolve, reject) => {
        const user = this.getUser(uid);
        user ? resolve(user) : reject(new Error(`No user found with uid: ${uid}`));
      });
    }

    public async getByContinueSignupToken(token: string): Promise<UserProfile | undefined> {
        return this.users.find((user: UserProfile) => user.continueSignupToken === token);
    }

    public getUser(uid: string): UserProfile | undefined {
        const found = this.users.find((user: UserProfile) => user.uid === uid);

        if (found != null) {
            return UserProfile.createUserProfileFromDbObject(found);
        }

        return found;
    }

    public add(userProfile: UserProfile): Promise<void> {
        return new Promise((resolve) => {
            this.users.push(userProfile);
            resolve();
        });
    }

    public update(profile: UserProfile): Promise<UserProfile> {
        return new Promise<UserProfile>((resolve, reject) => {
            const index = this.users.findIndex((user: UserProfile) => user.uid === profile.uid);
            if (index < 0) {
                reject(new Error(`No alert with uid ${profile.uid} found`));
            }
            this.users[index] = Object.assign(this.users[index], profile);
            resolve(this.users[index]);
        });
    }

    public search(searchQuery: string): Promise<UserProfile[]> {
        return new Promise((resolve) => {
            resolve(this.users.filter((user: UserProfile) =>
                (user.email && user.email.toLowerCase() === (searchQuery.toLowerCase())) ||
                (user.fullname && user.fullname.toLowerCase().includes(searchQuery.toLowerCase())) ||
                (user.nickname && user.nickname.toLowerCase().includes(searchQuery.toLowerCase())))
                .map((u) => UserProfile.createUserProfileFromDbObject(u)));
        });
    }

    public getAll(): Promise<UserProfile[]> {
        return new Promise((resolve) => {
            resolve(this.users);
        });
    }

    public getByStudioBeltNumber(beltNumber: string): Promise<UserProfile[]> {
        return Promise.resolve(this.users.filter((user: UserProfile) => {
            return user.heartRateMonitors?.studio === beltNumber;
        }));
    }

    public getUserByEmail(email: string): Promise<UserProfile | undefined> {
        return Promise.resolve(this.users.find((user: UserProfile) => {
            return user.email === email;
        }));
    }

    public async getUserByWorkoutImportConfig(
        origin: WorkoutImportOrigin,
        userId: string,
    ): Promise<UserProfile> {
        throw new Error('Method not implemented.');
    };

    public getUsersByEmails(emails: string[]): Promise<UserProfile[]> {
        return Promise.resolve(this.users.filter((user) => {
            return emails.indexOf(user.email) >= 0;
        }));
    }

    public getUsersByGymName(gymName?: string, emailExclusionFilter?: string): Promise<UserProfile[]> {
        return Promise.resolve(this.users.filter((user) => {
            return user.branch === gymName;
        }));
    }
    public getUsersByFilters(
        emailExclusionFilter?: string, planIds?: string[], includeOldAccounts?: boolean): Promise<UserProfile[]> {
        return Promise.resolve(this.users.filter((user) => {
            return emailExclusionFilter && !user.email.includes(emailExclusionFilter);
        }));
    }

    public getUsersByIds(userprofileIds: string[]): Promise<UserProfile[]> {
        return Promise.resolve(this.users.filter((user) => {
            return userprofileIds.indexOf(user.uid) >= 0;
        }));
    }

  public getPotentiallyDeletedUsersByIds(userprofileIds: string[]): Promise<(UserProfile | IAnonymousUser)[]> {
    throw new Error('Method not implemented.');
  }

  public removeContinueSignupToken(userId: string): Promise<UserProfile> {
        return new Promise<UserProfile>((resolve, reject) => {
            const index = this.users.findIndex((u: UserProfile) => u.uid === userId);
            if (index < 0) {
                reject(new Error(`No alert with uid ${userId} found`));
            }
            const user = this.users[index];
            delete user.continueSignupToken;
            this.users[index] = Object.assign(this.users[index], user);
            resolve(this.users[index]);
        });
    }

    public getAllUsersInFamilyPlan(planOwnerUserProfileId: string): Promise<UserProfile[]> {
      throw new Error('Method not implemented.');
    }

    public getUsersByUtmTagSource(source: string, medium: string): Promise<UserProfile[]> {
        throw new Error('Method not implemented.');
    }

    public async updateAccountComment(userId: string, newAccountComment: IAccountComment): Promise<IAccountComment[]> {
        const profile = await this.get(userId);
        if (!profile) {
            throw new Error('User not found');
        }
        const existingComments = profile.accountComments ? [...profile.accountComments] : [];
        existingComments.push(newAccountComment);
        profile.accountComments = existingComments;
        const changedProfile = await this.update(profile);
        return changedProfile.accountComments ? changedProfile.accountComments : [];
    }

    public async updateFollowUpDate(userId: string, followUpDate: Date): Promise<Date | undefined> {
        const profile = await this.get(userId);
        if (!profile) {
            throw new Error('User not found');
        }
        profile.followUpDate = followUpDate;
        const changedProfile = await this.update(profile);
        return changedProfile.followUpDate;
    }

    public async removeFollowUpDate(userId: string): Promise<void> {
        const profile = await this.get(userId);
        if (!profile) {
            throw new Error('User not found');
        }
        delete profile.followUpDate;
        await this.update(profile);
    }

    public async getFollowUpDate(userId: string): Promise<Date | undefined> {
        const profile = await this.get(userId);
        if (!profile) {
            throw new Error('User not found');
        }
        return profile.followUpDate;
    }

    public async getSingleUserForTesting(): Promise<any> {
      return Promise.resolve(() => {
        return this.users[0];
      });
    }

    public getUserByEnrolledFaceId(enrolledFaceId: string): Promise<UserProfile | undefined> {
      return new Promise<UserProfile>((resolve, reject) => {
        const users = this.users.filter((user: UserProfile) => user.enrolledFaceId === enrolledFaceId);

        if (users.length !== 1) {
          reject(new Error(`no user found with face id ${enrolledFaceId}`));
        }

        resolve(users[0]);
      });
    }

    public getUsersSignedUpAfter(requestTime: Date, signupDate: Date, gymName: string): Promise<UserProfile[]> {
        throw new Error('Method not implemented.');
    }

    public getAllUsersInTeam(team: string): Promise<IUserNames[]> {
        throw new Error('Method not implemented.');
    }

    public getLeaderboardPreferencesForUsersByLeaderboardId(
      leaderboardId: string,
    ): Promise<IUserLeaderboardPreferenceViewResult[]> {
      throw new Error('Method not implemented');
    }
}
